import React from 'react'
import * as Sentry from '@sentry/nextjs'
import Flex from 'shared/library/atoms/Flex'
import Icon from 'shared/library/atoms/Icon'
import { H3, Text } from 'library/atoms/Typography'
import Button from 'library/atoms/Button'
import styled from '@emotion/styled'
import useTranslation from 'next-translate/useTranslation'
import { MutationFunction, useMutation } from '@apollo/client'
import {
  Baskets_Price_Modifiers,
  Basket_Response,
  Redemption_Codes
} from 'shared/presenters/graphqlTypes'
import useRemovePriceModifier from 'state/useRemovePriceModifier'
import { RecursivePartial } from 'shared/presenters/presenter'
import { Translate } from 'next-translate'
import { useRouter } from 'next/router'
import { BasketContextProps, FETCH_BASKET } from 'providers/BasketProvider'
import { REMOVE_BASKET_ITEM } from 'library/organisms/Basket'
import widgetRoutes from 'enums/routes'
import { definedNotNull } from 'shared/presenters/presenter'
import { HttpHeader } from 'shared/enums/httpHeader'

export interface ModalContentActiveVoucherInterface {
  onClose: () => void
  basketPriceModifier: RecursivePartial<Baskets_Price_Modifiers>
  changeViewOnSuccess: () => void
  basketId: string | undefined
  lineItemId?: string
  refetchBasket: BasketContextProps['refetchBasket']
  activityVoucherCode?: Redemption_Codes['code']
}

const ModalContentActiveVoucher = ({
  onClose,
  basketId,
  lineItemId,
  basketPriceModifier,
  changeViewOnSuccess
}: ModalContentActiveVoucherInterface) => {
  const { t } = useTranslation('chargeable')
  const router = useRouter()
  const widgetSlug = router?.query?.widgetSlug as string
  const { locale } = router

  const determinePriceModifierData = ({
    basketPriceModifier
  }: {
    basketPriceModifier: RecursivePartial<Baskets_Price_Modifiers>

    t: Translate
  }) => {
    const code = basketPriceModifier?.redemption_code?.code
    const creditVoucher = basketPriceModifier?.credit_voucher ?? undefined
    const purchasedClassPass =
      basketPriceModifier?.purchased_class_pass ?? undefined

    return {
      code,
      creditVoucher,
      purchasedClassPass
    }
  }

  const { code } = determinePriceModifierData({
    basketPriceModifier,
    t
  })

  const { removePriceModifier, removePriceModifierLoading } =
    useRemovePriceModifier()

  const removePriceModifierOnClick = async ({
    basketId,
    lineItemId,
    code,
    removePriceModifier
  }: {
    basketId?: Basket_Response['basket_id']
    lineItemId?: string
    code: Redemption_Codes['code']
    removePriceModifier: MutationFunction
  }) => {
    if (!basketId) {
      return
    }

    try {
      await removePriceModifier({
        variables: {
          // TODO: The lineItemId isn't necessary for removing a credit voucher
          // Uncomment once we are dealing with more price_modifiers that uses this id to remove the discount
          // lineItemId: itemId,
          basketId,
          code,
          lineItemId
        }
      })
    } catch (err) {
      // We have to verify err is an
      // error before using it as one.
      if (err instanceof Error) {
        Sentry.captureMessage(err.message)
      }
    }
  }

  const handleChangeViewOnSuccess = () => {
    changeViewOnSuccess()
  }

  const handleOnClose = () => {
    onClose()
  }

  const [removeBasketItem] = useMutation<{
    Consumer_Baskets_RemoveItem: Basket_Response
  }>(REMOVE_BASKET_ITEM, {
    refetchQueries: [
      {
        query: FETCH_BASKET,
        variables: { basketId, locale },
        context: { headers: { [HttpHeader.xHasuraBasketId]: basketId } }
      }
    ]
  })

  const removeBasketItemOnClick = async (lineItemId: string) => {
    if (!basketId) {
      return
    }

    try {
      await removeBasketItem({
        variables: {
          lineItemId
        }
      })
    } catch (err) {
      // catching here to protect against page breaks
    }
  }

  return (
    <>
      <StyledDiv>
        <Icon name="voucher" size={52} color="gold" />
        <H3 mt={5} mb={1}>
          {t('voucher-active')}
        </H3>
        <Text fontStyle="p2" color="secondary">
          {t('if-remove-voucher')}
        </Text>
      </StyledDiv>
      <Flex flexDirection="column" justifyContent="center" alignItems="center">
        {lineItemId ? (
          <StyledButton
            variant="primary"
            onClick={() => {
              router.push(widgetRoutes.HOME.url({ widgetSlug }))
              removeBasketItemOnClick(lineItemId)
              handleOnClose()
              handleChangeViewOnSuccess()
            }}
          >
            {t('remove-voucher')}
          </StyledButton>
        ) : (
          <StyledButton
            variant="primary"
            onClick={() => {
              if (removePriceModifierLoading) {
                return
              }
              removePriceModifierOnClick({
                basketId,
                lineItemId,
                code: definedNotNull(code),
                removePriceModifier
              })
              handleOnClose()
              handleChangeViewOnSuccess()
            }}
          >
            {t('remove-voucher')}
          </StyledButton>
        )}
        <StyledButton
          variant="secondary"
          onClick={() => {
            handleOnClose()
          }}
          mt={3}
          aria-label={t('cancel-remove-voucher')}
        >
          {t('common:cancel')}
        </StyledButton>
      </Flex>
    </>
  )
}

export default ModalContentActiveVoucher

const StyledDiv = styled.div(({ theme }) => ({
  marginBottom: theme.space[5],
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  textAlign: 'center'
}))

const StyledButton = styled(Button)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.radii[2]
}))
